import useControlledState from '@atlas/tool/box/hooks/useControlledState';
import useLastRef from '@atlas/tool/box/hooks/useLastRef';
import { FormContext } from '@atlas/uicorn/lib/Control/Form';
import Popover from '@atlas/uicorn/lib/Display/Popover';
import PaintIcon from '@atlas/uicorn/lib/Icon/PaintIcon';
import type { TExample } from '@atlas/uicorn/preview/modules';
import { debounce } from 'lodash-es';
import { useContext, useMemo, useState } from 'react';
import * as React from 'react';
import styled, { css } from 'styled-components';
import useRefCallback from 'use-ref-callback';

const ColorInput = React.memo(
    React.forwardRef(function ColorInput(
        props: TColorInputProps,
        ref: React.ForwardedRef<TColorInputRef>,
    ): React.ReactElement {
        const {
            className,
            style,
            value: propsValue,
            onChange: propsOnChange,
            defaultValue,
            placeholder,
            onFocus,
            onBlur,
            onClick,
            disabled: propsDisabled,
        } = props;

        const { disabled: contextDisabled } = useContext(FormContext);

        const disabled = propsDisabled ?? contextDisabled;

        const [value, onChange] = useControlledState(propsValue, propsOnChange, defaultValue);

        const [focus, setFocus] = useState(false);
        const handleFocus = useRefCallback((event: React.FocusEvent<HTMLInputElement>) => {
            onFocus?.(event);
            if (event.defaultPrevented) return;
            setFocus(true);
        });
        const handleBlur = useRefCallback((event: React.FocusEvent<HTMLInputElement>) => {
            onBlur?.(event);
            if (event.defaultPrevented) return;
            setFocus(false);
        });

        const handleClick = useRefCallback((event: React.MouseEvent<HTMLInputElement>) => {
            onClick?.(event);
            event.currentTarget.querySelector('input')?.focus();
        });

        const handleChange = useRefCallback((event: React.ChangeEvent<HTMLInputElement>) =>
            onChange(event.target.value),
        );

        return (
            <SColorInputPopover content={<HexColorPicker value={value} onChange={onChange} />}>
                <SColorInput
                    className={className}
                    style={style}
                    onClick={handleClick}
                    {...(focus && { ['data-focus']: true })}
                    $disabled={disabled}
                >
                    <SColorInputPreview $color={isValidHexColor(value) ? value : undefined} />
                    <SColorInputInput
                        value={value}
                        placeholder={placeholder}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        ref={ref}
                    />
                    <SColorInputIcon />
                </SColorInput>
            </SColorInputPopover>
        );
    }),
);

export default ColorInput;

// #region Helpers

const ReactColorful = React.lazy(() => import('react-colorful').then((module) => ({ default: module.HexColorPicker })));
function HexColorPicker({ value, onChange }: { value?: string; onChange?: (value: string) => void }) {
    const onChangeRef = useLastRef(onChange);
    const change = useMemo(() => debounce((value: string) => onChangeRef.current?.(value), 100), [onChangeRef]);
    return (
        <React.Suspense fallback="...">
            <SReactColorful color={value} onChange={change} />
        </React.Suspense>
    );
}

function isValidHexColor(value: string) {
    return /^#[0-9A-F]{6}$/i.test(value);
}

// #endregion

// #region Styles

const emptyBackgroundCssValue = (greyColor: string) => `
    linear-gradient(45deg, ${greyColor} 25%, transparent 25%),
    linear-gradient(-45deg, ${greyColor} 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, ${greyColor} 75%),
    linear-gradient(-45deg, transparent 75%, ${greyColor} 75%)
`;

const SReactColorful = styled(ReactColorful)`
    &.react-colorful {
        border-radius: inherit;
        width: 20rem;
        height: 20rem;
    }

    .react-colorful__saturation {
        border-top-left-radius: inherit;
        border-top-right-radius: inherit;
        border-bottom-width: 1.2rem;
    }

    .react-colorful__alpha-gradient,
    .react-colorful__saturation {
        box-shadow: inset 0 0 0 0.1rem rgba(0, 0, 0, 0.05);
    }

    .react-colorful__alpha,
    .react-colorful__hue {
        height: 2.4rem;
    }

    .react-colorful__last-control {
        border-bottom-left-radius: inherit;
        border-bottom-right-radius: inherit;
    }

    .react-colorful__pointer {
        width: 2.8rem;
        height: 2.8rem;
        border-width: 0.2rem;
        box-shadow: 0 0.2rem 0.4rem rgba(0, 0, 0, 0.2);
    }
`;

const SColorInputIcon = styled(PaintIcon)`
    color: ${(p) => p.theme.color.grey8};
`;

const SColorInputInput = styled.input`
    flex: 1;
    background-color: transparent;
    border: none;
    box-shadow: none;
    outline: none;
    padding: 0;
    min-width: 8rem;
`;

const SColorInputPreview = styled.span<{ $color?: string }>`
    flex-shrink: 0;
    background: ${(p) => p.$color || emptyBackgroundCssValue(p.theme.color.grey7)};
    background-size: 1rem 1rem;
    background-position: 0 0, 10px 0, 10px -10px, 0px 10px;
    outline: ${(p) => (p.$color ? `0.1rem solid ${p.theme.color.grey6}` : 'none')};

    display: inline-block;
    width: 2.2rem;
    height: 2.2rem;
    border-radius: 0.5rem;
`;

export const SColorInput = styled.div<{ $disabled: boolean }>`
    ${(p) => p.theme.patch.input}
    display: inline-flex;
    align-items: center;
    gap: 0.8rem;

    ${(p) =>
        p.$disabled
            ? css`
                  opacity: 0.5;
                  pointer-events: none;
                  cursor: not-allowed;
              `
            : ''}
`;

const SColorInputPopover = styled(Popover)``;

// #endregion

// #region Types

export type TColorInputProps = Pick<React.HTMLAttributes<HTMLInputElement>, 'onFocus' | 'onBlur'> &
    Pick<React.HTMLAttributes<HTMLDivElement>, 'onClick'> & {
        className?: string;
        style?: React.CSSProperties;
        value?: string;
        placeholder?: string;
        defaultValue?: string;
        disabled?: boolean;
        onChange?: (newValue: string) => void;
    };

export type TColorInputRef = HTMLInputElement;

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        return <ColorInput onChange={props.onChange} />;
    },
    handlers: {
        onChange: 'console',
    },
    figma: 'https://www.figma.com/file/YK0CuM3dK7LcMbVtTUuL8I/Product----Web-2.0?node-id=2215:13758&mode=dev',
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('ColorInput', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
